import { resolveKnownMultiSelectTokens } from '../../common/utils/reportTypeTokens';
import { buildAccessSettingFromWrPublicSetting, parseWrPublicSettingOptionIds, WrPublicSettingRow } from '../../../lib/weeklyReportOrganizationAccess';
import { restoreLegacyWeeklyReportScopeTargets } from '../../../lib/weeklyReportScopeTargets';

export type ReportTypeVisibility = 'private' | 'dept' | 'team' | 'public';

/** 公開設定の対象 option の情報。無効化された option も分類名から解決するために保持する。 */
export type ReportTypeAccessOptionMeta = {
  id: string;
  label: string;
  value: string;
  department_id: string;
  team_id: string;
  status?: string | null;
};

export type ReportTypeAccessSetting = {
  visibility: ReportTypeVisibility;
  departments: string[];
  teams: string[];
  /** アクセスマップのエントリにだけ付く。判定には使わない。 */
  option?: ReportTypeAccessOptionMeta;
};

export type ReportTypeAccessCandidate = {
  id?: unknown;
  label?: unknown;
  value?: unknown;
  department_id?: unknown;
  departmentId?: unknown;
  team_id?: unknown;
  teamId?: unknown;
};

export const getWeeklyReportRegisteredDepartmentRawValues = (
  row: Record<string, unknown> | null | undefined
): unknown[] => {
  const anyRow = row ?? {};
  return [
    anyRow.department,
    anyRow.department_id,
    anyRow.departmentId,
    anyRow.report_department,
    anyRow.reportDepartment,
    anyRow.registered_department,
    anyRow.registeredDepartment,
    anyRow.division_id,
    anyRow.divisionId,
    anyRow.department_code,
    anyRow.departmentCode,
    anyRow.department_name,
    anyRow.departmentName,
    anyRow.dept,
    anyRow.division,
    anyRow.division_name,
    anyRow.divisionName,
  ];
};

const normalizeOrgId = (raw: unknown): string | null => {
  const source = String(raw ?? '').trim();
  if (!source) return null;
  if (!/^\d+$/.test(source)) return null;
  const n = Number(source);
  if (!Number.isFinite(n)) return null;
  return String(n);
};

const candidateDepartmentId = (candidate: ReportTypeAccessCandidate): string | null =>
  normalizeOrgId(candidate.department_id ?? candidate.departmentId);

const candidateTeamId = (candidate: ReportTypeAccessCandidate): string | null =>
  normalizeOrgId(candidate.team_id ?? candidate.teamId);

const matchesCandidateToken = (
  candidate: ReportTypeAccessCandidate,
  token: unknown
): boolean => {
  const normalizedToken = String(token ?? '').trim();
  if (!normalizedToken) return false;

  return (
    String(candidate.value ?? candidate.label ?? '').trim() === normalizedToken ||
    String(candidate.label ?? '').trim() === normalizedToken
  );
};

const dedupeCandidatesById = <T extends ReportTypeAccessCandidate>(
  candidates: T[]
): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];

  candidates.forEach((candidate, index) => {
    const id = String(candidate.id ?? '').trim();
    const fallbackKey = [
      candidateDepartmentId(candidate) ?? '',
      candidateTeamId(candidate) ?? '',
      String(candidate.label ?? '').trim(),
      String(candidate.value ?? '').trim(),
      index,
    ].join('::');
    const key = id || fallbackKey;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(candidate);
  });

  return out;
};

export const selectReportTypeAccessCandidates = <
  T extends ReportTypeAccessCandidate,
>(
  reportTypes: T[] | null | undefined,
  args: {
    token: unknown;
    departmentId: unknown;
    teamId?: unknown;
  }
): T[] => {
  const rows = Array.isArray(reportTypes) ? reportTypes : [];
  const deptId = normalizeOrgId(args.departmentId);
  const teamId = normalizeOrgId(args.teamId);

  if (!deptId) return [];

  const byDepartment = rows.filter((rt) => {
    const reportTypeDeptId = candidateDepartmentId(rt);
    if (reportTypeDeptId !== deptId) return false;
    return matchesCandidateToken(rt, args.token);
  });

  if (byDepartment.length === 0) return [];

  if (teamId) {
    const byExactTeam = byDepartment.filter((rt) => candidateTeamId(rt) === teamId);
    if (byExactTeam.length > 0) return dedupeCandidatesById(byExactTeam);
  }

  const byDepartmentCommon = byDepartment.filter((rt) => {
    const reportTypeTeamId = candidateTeamId(rt);
    return reportTypeTeamId == null || reportTypeTeamId === '0';
  });
  if (byDepartmentCommon.length > 0) {
    return dedupeCandidatesById(byDepartmentCommon);
  }

  if (teamId) return [];

  return dedupeCandidatesById(byDepartment);
};

const candidateLabels = (candidate: ReportTypeAccessCandidate): string[] =>
  Array.from(
    new Set(
      [candidate.value, candidate.label]
        .map((value) => String(value ?? '').trim())
        .filter((value) => value.length > 0)
    )
  );

export const resolveReportTypeTokens = (
  raw: unknown,
  reportTypes: ReportTypeAccessCandidate[] | null | undefined,
  args: {
    departmentId: unknown;
    teamId?: unknown;
  }
): string[] => {
  const source = String(raw ?? '').trim();
  if (!source) return [];

  const departmentId = normalizeOrgId(args.departmentId);
  const teamId = normalizeOrgId(args.teamId);
  const scopedRows = (Array.isArray(reportTypes) ? reportTypes : []).filter((candidate) => {
    if (candidateDepartmentId(candidate) !== departmentId) return false;
    const candidateTeam = candidateTeamId(candidate);
    return !teamId || candidateTeam === teamId || candidateTeam == null || candidateTeam === '0';
  });
  const knownLabels = Array.from(new Set(scopedRows.flatMap(candidateLabels))).sort(
    (a, b) => b.length - a.length
  );
  return resolveKnownMultiSelectTokens(source, knownLabels);
};

/**
 * 週報に保存された文字列を、課・チームに対応するマスタの option_id 群へ解決する。
 *
 * 1つの正式名称に同一スコープの候補が複数ある場合があるため、名称単位の配列を
 * 維持する。呼び出し側は同じグループ内の明示的な公開設定を優先できる。
 */
export const resolveReportTypeOptionIdGroups = (
  raw: unknown,
  reportTypes: ReportTypeAccessCandidate[] | null | undefined,
  args: {
    departmentId: unknown;
    teamId?: unknown;
  }
): string[][] => {
  const tokens = resolveReportTypeTokens(raw, reportTypes, args);

  return tokens
    .map((token) => {
      const seen = new Set<string>();
      return selectReportTypeAccessCandidates(reportTypes, {
        token,
        departmentId: args.departmentId,
        teamId: args.teamId,
      })
        .map((candidate) => String(candidate.id ?? '').trim())
        .filter((optionId) => {
          if (!optionId || seen.has(optionId)) return false;
          seen.add(optionId);
          return true;
        });
    })
    .filter((optionIds) => optionIds.length > 0);
};

/** 名称グループを平坦化した option_id 一覧。登録順に依存せず重複を除去する。 */
export const resolveReportTypeOptionIds = (
  raw: unknown,
  reportTypes: ReportTypeAccessCandidate[] | null | undefined,
  args: {
    departmentId: unknown;
    teamId?: unknown;
  }
): string[] => {
  const seen = new Set<string>();
  return resolveReportTypeOptionIdGroups(raw, reportTypes, args)
    .flat()
    .filter((optionId) => {
      if (seen.has(optionId)) return false;
      seen.add(optionId);
      return true;
    });
};

const mergeUniqueCodes = (a: string[], b: string[]): string[] =>
  Array.from(new Set([...(a ?? []), ...(b ?? [])]));

const hasPartialVisibility = (setting: ReportTypeAccessSetting): boolean =>
  setting.visibility === 'dept' || setting.visibility === 'team';

const hasTeamScope = (setting: ReportTypeAccessSetting): boolean =>
  (setting.teams ?? []).some((v) => String(v ?? '').trim().length > 0);

export const mergeAccessSettingByUnion = (
  current: ReportTypeAccessSetting,
  candidate: ReportTypeAccessSetting
): ReportTypeAccessSetting => {
  if (current.visibility === 'public' || candidate.visibility === 'public') {
    return {
      visibility: 'public',
      departments: [],
      teams: [],
    };
  }

  const departments = mergeUniqueCodes(current.departments, candidate.departments);
  const teams = mergeUniqueCodes(current.teams, candidate.teams);
  const hasPartial = hasPartialVisibility(current) || hasPartialVisibility(candidate);

  if (!hasPartial) {
    return {
      visibility: 'private',
      departments: [],
      teams: [],
    };
  }

  return {
    visibility: hasTeamScope({ visibility: 'team', departments, teams }) ? 'team' : 'dept',
    departments,
    teams,
  };
};

const buildOptionMetaFromRow = (
  optionId: string,
  row: WrPublicSettingRow
): ReportTypeAccessOptionMeta | undefined => {
  const value = String(row.option_value ?? '').trim();
  const label = String(row.option_label ?? '').trim();
  const departmentId = normalizeOrgId(row.option_department_id);
  if (!value && !label) return undefined;
  if (!departmentId) return undefined;
  return {
    id: optionId,
    label: label || value,
    value: value || label,
    department_id: departmentId,
    // 0 は課共通。値が無い場合も課共通として扱う。
    team_id: normalizeOrgId(row.option_team_id) ?? '0',
    status: row.option_status == null ? null : String(row.option_status),
  };
};

export const buildWeeklyReportAccessMap = (
  rows: WrPublicSettingRow[],
  knownIds: ReadonlySet<string>,
  masters: { departments?: any[]; teams?: any[] },
): Record<string, ReportTypeAccessSetting> => {
  const result: Record<string, ReportTypeAccessSetting> = {};
  rows.forEach((row) => {
    const restored = restoreLegacyWeeklyReportScopeTargets(row, masters);
    const setting = buildAccessSettingFromWrPublicSetting(restored);
    parseWrPublicSettingOptionIds(row.option_id, knownIds).forEach((id) => {
      const merged = result[id] ? mergeAccessSettingByUnion(result[id], setting) : setting;
      const option = result[id]?.option ?? buildOptionMetaFromRow(id, row);
      result[id] = option ? { ...merged, option } : merged;
    });
  });
  return result;
};

const PUBLIC_ACCESS_SETTING: ReportTypeAccessSetting = {
  visibility: 'public',
  departments: [],
  teams: [],
};

/**
 * 有効な分類マスタに、アクセスマップ由来の option (無効化済みを含む) を合成する。
 * 無効化された option の公開設定は、過去の週報に対して維持するため、その分類名も
 * option に解決できる必要がある。有効な分類マスタには無効 option が含まれない。
 */
export const resolveReportTypeAccessOptions = <T extends ReportTypeAccessCandidate>(
  reportTypes: T[] | null | undefined,
  accessMap: Record<string, ReportTypeAccessSetting> | null | undefined
): ReportTypeAccessCandidate[] => {
  const base: ReportTypeAccessCandidate[] = Array.isArray(reportTypes) ? [...reportTypes] : [];
  const known = new Set(base.map((option) => String(option.id ?? '').trim()));
  Object.entries(accessMap ?? {}).forEach(([optionId, setting]) => {
    const option = setting?.option;
    if (!option || known.has(optionId)) return;
    known.add(optionId);
    base.push({
      id: optionId,
      label: option.label,
      value: option.value,
      department_id: option.department_id,
      team_id: option.team_id,
    });
  });
  return base;
};

export type EffectiveReportTypeAccess = {
  /** 直接適用する設定の和集合 (表示・互換用)。判定には directSettings / fallbackGroups を使う。 */
  setting: ReportTypeAccessSetting;
  /** 週報のチーム(または課共通)の option の設定。どれか 1 つが許可なら閲覧可。 */
  directSettings: ReportTypeAccessSetting[];
  /**
   * 週報のチームに同名 option が無い分類の設定 (同課の他チームの同名 option)。
   * 分類ごとに最も厳しい設定で判定する (グループ内の全件が許可のときだけ許可)。
   */
  fallbackGroups: ReportTypeAccessSetting[][];
  /** すべての分類が明示的な公開設定に解決できた。 */
  configured: boolean;
};

const lookupAccessSetting = (
  accessMap: Record<string, ReportTypeAccessSetting>,
  optionId: unknown
): ReportTypeAccessSetting | undefined => {
  const raw = String(optionId ?? '').trim();
  if (!raw) return undefined;
  const candidates = [raw, String(Number(raw))];
  const hit = candidates.find((candidate) =>
    Object.prototype.hasOwnProperty.call(accessMap, candidate)
  );
  return hit === undefined ? undefined : accessMap[hit];
};

/**
 * 週報に保存された分類名から、公開範囲の判定に使う設定を解決する。
 *
 * 同名の分類はチームごとに別の設定を持つ (サーバーの
 * _classify_wr_option_for_report_team と同じ規則)。
 * 1. 週報のチームに同名のチーム固有 option があれば、それだけを使う。
 * 2. なければ、同名の課共通 (team 0) option があればそれだけを使う。
 * 3. どちらも無ければ、同課の同名 option (他チーム) を fallback として、最も厳しい
 *    設定で判定する。分類を黙って無視すると既定の全公開に落ちるため。
 * 他チームの option は、1 または 2 が成立する間は借りない。
 * 無効化された option も、過去の週報に対して設定を維持する。
 */
export const resolveEffectiveAccessForReportTypeValue = (args: {
  raw: unknown;
  options: ReportTypeAccessCandidate[] | null | undefined;
  departmentId: unknown;
  teamId?: unknown;
  accessMap: Record<string, ReportTypeAccessSetting>;
}): EffectiveReportTypeAccess => {
  const unresolvedDefault: EffectiveReportTypeAccess = {
    setting: PUBLIC_ACCESS_SETTING,
    directSettings: [],
    fallbackGroups: [],
    configured: false,
  };
  const source = String(args.raw ?? '').trim();
  const departmentId = normalizeOrgId(args.departmentId);
  if (!source || !departmentId) return unresolvedDefault;

  const options = Array.isArray(args.options) ? args.options : [];
  const teamId = normalizeOrgId(args.teamId);
  // 他チームにしか無い分類名も分割できるよう、分類名の解決には課全体の option を使う。
  const tokens = resolveReportTypeTokens(source, options, { departmentId });
  if (tokens.length === 0) return unresolvedDefault;

  const directSettings: ReportTypeAccessSetting[] = [];
  const fallbackGroups: ReportTypeAccessSetting[][] = [];
  let configured = true;

  const settingsOf = (candidates: ReportTypeAccessCandidate[]) => {
    const resolved = dedupeCandidatesById(candidates).map((candidate) => {
      const setting = lookupAccessSetting(args.accessMap, candidate.id);
      return { configured: setting !== undefined, setting: setting ?? PUBLIC_ACCESS_SETTING };
    });
    const explicit = resolved.filter((entry) => entry.configured);
    return {
      configured: explicit.length > 0,
      settings: (explicit.length > 0 ? explicit : resolved).map((entry) => entry.setting),
    };
  };

  tokens.forEach((token) => {
    const sameName = options.filter(
      (option) =>
        candidateDepartmentId(option) === departmentId && matchesCandidateToken(option, token)
    );
    const exact = teamId ? sameName.filter((option) => candidateTeamId(option) === teamId) : [];
    const common = sameName.filter((option) => {
      const optionTeamId = candidateTeamId(option);
      return optionTeamId == null || optionTeamId === '0';
    });
    const direct = exact.length > 0 ? exact : common;

    if (direct.length > 0) {
      const { configured: groupConfigured, settings } = settingsOf(direct);
      directSettings.push(...settings);
      if (!groupConfigured) configured = false;
      return;
    }
    if (sameName.length > 0) {
      const { configured: groupConfigured, settings } = settingsOf(sameName);
      fallbackGroups.push(settings);
      if (!groupConfigured) configured = false;
      return;
    }
    // どの option にも解決できない分類名。判定には使わないが、設定済みとは見なさない。
    configured = false;
  });

  if (directSettings.length === 0 && fallbackGroups.length === 0) {
    return unresolvedDefault;
  }

  const union = directSettings.reduce<ReportTypeAccessSetting>(
    mergeAccessSettingByUnion,
    { visibility: 'private', departments: [], teams: [] }
  );
  return { setting: union, directSettings, fallbackGroups, configured };
};

/**
 * 解決した設定で、閲覧可否を判定する。分類の間はどれか 1 つが許可なら閲覧可 (和集合)。
 * ただし fallback の分類は、同名の設定が 1 つでも拒否なら拒否 (最も厳しい設定)。
 * 判定自体は canAccessSetting に委ね、週報の課や閲覧者の組織はそちらで扱う。
 */
export const isAccessAllowedByEffectiveAccess = (
  access: EffectiveReportTypeAccess,
  canAccessSetting: (setting: ReportTypeAccessSetting) => boolean
): boolean => {
  const decisions = [
    ...access.directSettings.map((setting) => canAccessSetting(setting)),
    ...access.fallbackGroups.map((group) => group.every((setting) => canAccessSetting(setting))),
  ];
  if (decisions.length === 0) return canAccessSetting(access.setting);
  return decisions.some(Boolean);
};
